/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * MIRRORED BARS — Symmetrical frequency spectrum with glow
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Same concept as FrequencyBars but mirrored vertically — bars grow UP and DOWN
 * from the horizontal center line. This creates a satisfying symmetry.
 *
 * Added effect: a soft glow/bloom on each bar using shadowBlur, which makes the
 * bars look like they're emitting light. The glow intensity pulses with beats.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

export class MirroredBars implements Visualization {
  id = 'mirrored-bars';
  name = 'Mirrored Bars';
  category = 'spectrum' as const;
  description = 'Symmetrical bars mirrored from center with glow effect';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private beatPulse = new BeatPulse(0.92);

  private barCount = 48;
  private gap = 2;
  private colorHue = 270;   // start purple
  private glowAmount = 12;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(frame: FrameData): void {
    const { frequencyData, isBeat, beatIntensity } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const centerY = height / 2;

    this.beatPulse.update(isBeat, beatIntensity);

    // Fade to black with slight trail (globalAlpha < 1 creates motion trails)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(0, 0, width, height);

    const totalGaps = (this.barCount - 1) * this.gap;
    const barWidth = Math.max(1, (width - totalGaps) / this.barCount);
    const usableBins = Math.floor(frequencyData.length * 0.55);
    const binsPerBar = usableBins / this.barCount;

    // Enable glow effect
    ctx.shadowBlur = this.glowAmount + this.beatPulse.value * 15;

    for (let i = 0; i < this.barCount; i++) {
      const startBin = Math.floor(i * binsPerBar);
      const endBin = Math.floor((i + 1) * binsPerBar);
      let sum = 0;
      for (let b = startBin; b < endBin; b++) sum += frequencyData[b];
      const amplitude = sum / (endBin - startBin) / 255;

      // Bar extends half-height from center (mirrored)
      const halfBar = amplitude * (height / 2) * 1.1;

      const hue = (i / this.barCount) * 180 + this.colorHue;
      const lightness = 50 + this.beatPulse.value * 20;
      const color = hsl(hue, 75, lightness);
      ctx.fillStyle = color;
      ctx.shadowColor = color;

      const x = i * (barWidth + this.gap);

      // Draw bar going UP from center
      ctx.fillRect(x, centerY - halfBar, barWidth, halfBar);
      // Draw bar going DOWN from center (mirror)
      ctx.fillRect(x, centerY, barWidth, halfBar);
    }

    // Reset shadow to avoid affecting other draws
    ctx.shadowBlur = 0;
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  destroy(): void {}

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'barCount', label: 'Bar Count', type: 'range', value: this.barCount, min: 16, max: 96, step: 8 },
      { key: 'gap', label: 'Gap', type: 'range', value: this.gap, min: 0, max: 6, step: 1 },
      { key: 'colorHue', label: 'Color Shift', type: 'range', value: this.colorHue, min: 0, max: 360, step: 10 },
      { key: 'glowAmount', label: 'Glow', type: 'range', value: this.glowAmount, min: 0, max: 30, step: 2 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.barCount === 'number') this.barCount = settings.barCount;
    if (typeof settings.gap === 'number') this.gap = settings.gap;
    if (typeof settings.colorHue === 'number') this.colorHue = settings.colorHue;
    if (typeof settings.glowAmount === 'number') this.glowAmount = settings.glowAmount;
  }
}
